import React, { Component } from 'react';
import _AdminService from 'modules/AdminService';
import _AnswerKeyService from 'modules/AnswerKeyService';
import _notification from 'modules/notification';
import { Card, Table, Modal, Tag, DatePicker } from 'antd';
import EmptyState from 'components/enterprise/EmptyState';
import LoadingSpinner from 'components/enterprise/LoadingSpinner';
import PageHeader from 'components/enterprise/PageHeader';
import DropdownList from 'components/controls/DropdownList';
import { routesAdmin } from 'routes/Routes';
import ConfirmPopup from '../popup/ConfirmPopup';
import ButtonComponent from 'components/controls/Button';
import FileUpload from 'components/controls/FileUpload';
import { GENERAL_ERROR_MESSAGE } from 'constants/constants';
import moment from 'moment';

// Phase C.9 (Answer Key & Lightweight Online Objection Workflow) — admin management for the Answer Key
// PDF publication lifecycle (Draft -> Publish -> Archive/Supersede), mirroring PublicationMaster_new.jsx's
// exact list+modal convention. Deliberately NOT a Question Bank/MCQ authoring screen — the admin only
// ever picks a Post/Batch, a First/Final type, an objection window (First only), and attaches the PDF
// exported from the separate Examination Platform.
const ANSWER_KEY_TYPES = [
    { Id: 'First', Name: 'First Answer Key' },
    { Id: 'Final', Name: 'Final Answer Key' }
];

const STATUS_TAG_COLOR = { Draft: undefined, Published: 'green', Archived: 'orange' };

class AnswerKeyMaster_new extends Component {
    constructor(props) {
        super(props);
        this.state = {
            pagination: { pageSize: 10, current: 1 },
            data: [],
            classRooms: [],
            classRoomOptions: [],
            loading: false,
            showCreateModal: false,
            supersedeTargetId: null,
            rowData: {},
            selectedFile: null,
            fileError: '',
            formError: '',
            showArchiveConfirm: false,
            archiveTargetId: null,
        };
    }

    componentDidMount() {
        this.getAll();
        this.loadBatchOptions();
    }

    getAll = async () => {
        this.setState({ loading: true });
        try {
            const response = await _AnswerKeyService.getAll();
            this.setState({ data: response.data || [], loading: false });
        } catch (e) {
            this.setState({ loading: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    // Batch options are labeled "Batch Name (Post) — Exam Date" so the admin can identify the right
    // Batch without a separate Post dropdown — PostId itself is derived server-side from the chosen
    // Batch's own Center -> Post relationship (see AnswerKeyService.ValidateMetadata).
    loadBatchOptions = async () => {
        try {
            const [classRoomResp, centerResp] = await Promise.all([
                _AdminService.GetAllClassRoom(),
                _AdminService.GetAllCenter()
            ]);
            const centerById = {};
            (centerResp.data || []).forEach(c => { centerById[c.Id] = c; });
            const classRooms = classRoomResp.data || [];
            const options = classRooms.map(cr => {
                const center = centerById[cr.Center_Id];
                const postName = center ? center.PostName : '';
                const dateLabel = cr.Exam_Date && moment(cr.Exam_Date).isValid() ? moment(cr.Exam_Date).format('DD-MMM-YYYY') : '';
                return { Id: cr.Id, Name: `${cr.Name}${postName ? ' (' + postName + ')' : ''}${dateLabel ? ' — ' + dateLabel : ''}` };
            });
            this.setState({ classRooms, classRoomOptions: options });
        } catch (e) {
            // non-fatal; the create form's Batch dropdown just stays empty until retried
        }
    }

    // Pagination bug fix (same class found across several admin masters): the Table's pagination
    // prop is bound to state, but without this onChange wired, clicking a page number never
    // actually updated `pagination.current` - the page selector changed visually but the rows didn't.
    onTableAction = (pagination) => {
        this.setState({ pagination });
    }

    onTypeChange = (event) => {
        this.setState({ rowData: { ...this.state.rowData, AnswerKeyType: event.target.value } });
    }

    onClassRoomChange = (event) => {
        const classRoomId = event.target.value ? parseInt(event.target.value, 10) : null;
        this.setState({ rowData: { ...this.state.rowData, ClassRoomId: classRoomId } });
    }

    onWindowStartChange = (date) => {
        this.setState({ rowData: { ...this.state.rowData, ObjectionWindowStart: date ? date.format("YYYY-MM-DDTHH:mm:ss") : "" } });
    }

    onWindowEndChange = (date) => {
        this.setState({ rowData: { ...this.state.rowData, ObjectionWindowEnd: date ? date.format("YYYY-MM-DDTHH:mm:ss") : "" } });
    }

    onFileDrop = (file) => {
        this.setState({ selectedFile: file, fileError: '' });
    }

    onFileError = (message) => {
        this.setState({ fileError: message, selectedFile: null });
    }

    openCreateModal = () => {
        this.setState({ showCreateModal: true, supersedeTargetId: null, rowData: {}, selectedFile: null, fileError: '', formError: '' });
    }

    openSupersedeModal = (record) => {
        this.setState({
            showCreateModal: true,
            supersedeTargetId: record.Id,
            rowData: {
                AnswerKeyType: record.AnswerKeyType,
                ClassRoomId: record.ClassRoomId,
                ObjectionWindowStart: record.ObjectionWindowStart,
                ObjectionWindowEnd: record.ObjectionWindowEnd
            },
            selectedFile: null,
            fileError: '',
            formError: ''
        });
    }

    closeCreateModal = () => {
        this.setState({ showCreateModal: false, supersedeTargetId: null, rowData: {}, selectedFile: null, fileError: '', formError: '' });
    }

    validate = () => {
        const { rowData, selectedFile } = this.state;
        if (!rowData.AnswerKeyType) { return 'Please select an Answer Key Type.'; }
        if (!rowData.ClassRoomId) { return 'Please select a Batch.'; }
        if (!selectedFile) { return 'Please attach a PDF document.'; }
        if (rowData.AnswerKeyType === 'First' && rowData.ObjectionWindowStart && rowData.ObjectionWindowEnd
            && moment(rowData.ObjectionWindowEnd).isBefore(moment(rowData.ObjectionWindowStart))) {
            return 'Objection window end cannot be earlier than the start.';
        }
        return '';
    }

    handleSave = async () => {
        const error = this.validate();
        if (error) {
            this.setState({ formError: error });
            return;
        }

        this.setState({ loading: true });
        try {
            const response = this.state.supersedeTargetId
                ? await _AnswerKeyService.supersede(this.state.supersedeTargetId, this.state.rowData, this.state.selectedFile)
                : await _AnswerKeyService.create(this.state.rowData, this.state.selectedFile);

            if (response.status === 200) {
                _notification.showMessage('success', this.state.supersedeTargetId
                    ? 'Replacement Answer Key created and published; the previous version has been archived.'
                    : 'Answer Key saved as Draft.');
                this.closeCreateModal();
                this.getAll();
            } else {
                this.setState({ loading: false });
                _notification.showMessage('error', (response.data && response.data.Message) || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            this.setState({ loading: false });
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    handlePublish = async (record) => {
        try {
            const response = await _AnswerKeyService.publish(record.Id);
            if (response.status === 200) {
                _notification.showMessage('success', 'Answer Key published.');
                this.getAll();
            } else {
                _notification.showMessage('error', (response.data && response.data.Message) || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    handleUnpublish = async (record) => {
        try {
            const response = await _AnswerKeyService.unpublish(record.Id);
            if (response.status === 200) {
                _notification.showMessage('success', 'Answer Key unpublished (hidden from candidates).');
                this.getAll();
            } else {
                _notification.showMessage('error', (response.data && response.data.Message) || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    onArchiveClick = (record) => {
        this.setState({ showArchiveConfirm: true, archiveTargetId: record.Id });
    }

    onConfirmArchive = async () => {
        const id = this.state.archiveTargetId;
        this.setState({ showArchiveConfirm: false, archiveTargetId: null });
        try {
            const response = await _AnswerKeyService.archive(id);
            if (response.status === 200) {
                _notification.showMessage('success', 'Answer Key archived.');
                this.getAll();
            } else {
                _notification.showMessage('error', (response.data && response.data.Message) || GENERAL_ERROR_MESSAGE);
            }
        } catch (e) {
            _notification.showMessage('error', GENERAL_ERROR_MESSAGE);
        }
    }

    render() {
        const { rowData, fileError, formError, supersedeTargetId, classRoomOptions } = this.state;

        const TableColumns = [
            { title: 'Post', dataIndex: 'PostName', key: 'PostName', width: '16%' },
            { title: 'Batch', dataIndex: 'ClassRoomName', key: 'ClassRoomName', width: '18%' },
            {
                title: 'Type', dataIndex: 'AnswerKeyType', key: 'AnswerKeyType', width: '10%',
                render: (value) => <Tag color={value === 'First' ? 'blue' : 'purple'}>{value}</Tag>
            },
            {
                title: 'Status', dataIndex: 'Status', key: 'Status', width: '10%',
                render: (value, record) => (
                    <>
                        <Tag color={STATUS_TAG_COLOR[value]}>{value}</Tag>
                        {value === 'Published' && !record.IsActive && <Tag color="red">Hidden</Tag>}
                    </>
                )
            },
            {
                title: 'Objection Window', dataIndex: '', key: 'window', width: '18%',
                render: (record) => record.ObjectionWindowStart && record.ObjectionWindowEnd
                    ? `${moment(record.ObjectionWindowStart).format('DD-MMM hh:mm A')} to ${moment(record.ObjectionWindowEnd).format('DD-MMM hh:mm A')}`
                    : '-'
            },
            {
                title: 'Action', dataIndex: '', key: 'Id', width: '20%',
                render: (record) => (
                    <>
                        {record.Status === 'Draft' && (
                            <ButtonComponent type="button" value="Publish" className="button-global button-primary small autoHeight mr-2 mb-1" onClick={() => this.handlePublish(record)} />
                        )}
                        {record.Status === 'Published' && record.IsActive && (
                            <ButtonComponent type="button" value="Unpublish" className="button-global button-border small autoHeight mr-2 mb-1" onClick={() => this.handleUnpublish(record)} />
                        )}
                        {record.Status === 'Published' && !record.IsActive && (
                            <ButtonComponent type="button" value="Republish" className="button-global button-primary small autoHeight mr-2 mb-1" onClick={() => this.handlePublish(record)} />
                        )}
                        {record.Status === 'Published' && (
                            <ButtonComponent type="button" value="Replace" className="button-global button-border small autoHeight mr-2 mb-1" onClick={() => this.openSupersedeModal(record)} />
                        )}
                        {record.Status !== 'Archived' && (
                            <ButtonComponent type="button" value="Archive" className="button-global button-danger small autoHeight mb-1" onClick={() => this.onArchiveClick(record)} />
                        )}
                    </>
                )
            },
        ];

        if (this.state.loading && this.state.data.length === 0)
            return <LoadingSpinner />

        return (
            <div className="container-fluid p-3">
                <PageHeader
                    title="Answer Key"
                    breadcrumbItems={[{ label: 'Dashboard', path: routesAdmin.dashboard.path }, { label: 'Answer Key' }]}
                />
                <div className="analysisWrap">
                    <div style={{ textAlign: 'right' }}>
                        <div className="max1200 m-auto mb-2">
                            <div className="d-flex align-items-center justify-content-end">
                                <a className="button-global button-border addbutton autoWidth d-inline-block text-center autoHeight mb-3 d-flex align-items-center justify-content-center"
                                    onClick={this.openCreateModal}>+ Add Answer Key</a>
                            </div>
                        </div>
                    </div>

                    <div className="max1200 m-auto">
                        <Table size='small'
                            bordered
                            rowKey='Id'
                            columns={TableColumns}
                            dataSource={this.state.data}
                            pagination={this.state.pagination}
                            onChange={this.onTableAction}
                            footer={() => <label style={{ textAlign: 'right', marginRight: '20px' }}>Total Records: {this.state.data.length}</label>}
                            className="table-striped-rows admin-table"
                            locale={{ emptyText: <EmptyState icon="inbox" title="No Answer Keys Found" description="No Answer Keys have been created yet." /> }}
                        />
                    </div>
                </div>

                <Modal
                    title={supersedeTargetId ? "Replace Answer Key (Supersede + Archive)" : "Add Answer Key"}
                    visible={this.state.showCreateModal}
                    onCancel={this.closeCreateModal}
                    footer={null}
                    width={600}
                    className="modelPopAnt admin-home"
                    destroyOnClose={true}
                >
                    <Card>
                        {supersedeTargetId && (
                            <p className="mb-3">
                                This creates a new Answer Key record linked to the one it replaces (e.g. a
                                corrected First Answer Key, or the Final Answer Key after objection review).
                                The previous version will be archived automatically once this one is published.
                            </p>
                        )}
                        <div className="mb-2">
                            <DropdownList
                                name="AnswerKeyType"
                                value={rowData.AnswerKeyType || ''}
                                onChange={this.onTypeChange}
                                placeholder="Select Answer Key Type"
                                heading="Answer Key Type"
                                isRequired={true}
                                options={ANSWER_KEY_TYPES}
                            />
                        </div>
                        <div className="mb-2">
                            <DropdownList
                                name="ClassRoomId"
                                value={rowData.ClassRoomId || ''}
                                onChange={this.onClassRoomChange}
                                placeholder="Select Batch"
                                heading="Batch"
                                isRequired={true}
                                options={classRoomOptions}
                            />
                        </div>
                        {rowData.AnswerKeyType === 'First' && (
                            <div className="row">
                                <div className="col-sm-6 mb-2">
                                    <label className="card-holder-name">Objection Window Start (optional)</label>
                                    <DatePicker showTime format="DD-MMM-YYYY hh:mm A" style={{ width: '100%' }} onChange={this.onWindowStartChange}
                                        defaultValue={rowData.ObjectionWindowStart && moment(rowData.ObjectionWindowStart).isValid() ? moment(rowData.ObjectionWindowStart) : undefined} />
                                </div>
                                <div className="col-sm-6 mb-2">
                                    <label className="card-holder-name">Objection Window End (optional)</label>
                                    <DatePicker showTime format="DD-MMM-YYYY hh:mm A" style={{ width: '100%' }} onChange={this.onWindowEndChange}
                                        defaultValue={rowData.ObjectionWindowEnd && moment(rowData.ObjectionWindowEnd).isValid() ? moment(rowData.ObjectionWindowEnd) : undefined} />
                                </div>
                                <div className="col-sm-12" style={{ fontSize: 12, color: '#888' }}>
                                    Candidates can only Raise Objection while both dates are set and the current time (IST) falls within this window.
                                </div>
                            </div>
                        )}
                        <div className="mb-2 mt-2">
                            <label className="card-holder-name">PDF Document (max 10 MB)<span className="color-red">*</span></label>
                            <FileUpload filetype="pdf" onFileDrop={this.onFileDrop} onError={this.onFileError} />
                            {fileError && <div className="color-red mt-1">{fileError}</div>}
                        </div>
                        {formError && <div className="color-red mb-2">{formError}</div>}
                        <div className="pt-2 buttonAlignCenter">
                            <ButtonComponent type="button" value="Cancel" onClick={this.closeCreateModal} className="button-global button-secondary mr-4 autoHeight small" />
                            <ButtonComponent type="button" value={supersedeTargetId ? "Save & Publish Replacement" : "Save as Draft"} onClick={this.handleSave} className="button-global button-primary autoHeight small" disabled={this.state.loading} />
                        </div>
                    </Card>
                </Modal>

                <Modal
                    centered={true}
                    width={400}
                    className="modelPopAnt admin-home"
                    visible={this.state.showArchiveConfirm}
                    onCancel={() => this.setState({ showArchiveConfirm: false, archiveTargetId: null })}
                    footer={false}
                >
                    <ConfirmPopup
                        onCancel={() => this.setState({ showArchiveConfirm: false, archiveTargetId: null })}
                        onOk={this.onConfirmArchive}
                        message="Are you sure you want to archive this Answer Key? It will no longer be visible to candidates. The record is retained for audit, not deleted."
                    />
                </Modal>
            </div>
        )
    };
}

export default AnswerKeyMaster_new;
